import React, { ReactElement } from 'react'
import { Link } from 'react-router'

import paths from '../../constants/paths'
import Company from '../../model/company'
import Button from '../elements/button'
import Headline from '../elements/Headline'
import Subtitle from '../elements/Subtitle'
import TitleMenu from '../elements/TitleMenu'

import './Terms.css'

type Props = {
  company?: Company
}

export default function PrivacyPolicy(props: Props): ReactElement | null {
  return (
    <div className="terms full-height">
      <div className="terms-text">
        <TitleMenu>
          {props.company && (
            <Link to={'/' + paths.COMPANIES + '/' + props.company.id + '/support'}>
              <Button>Tilbage</Button>
            </Link>
          )}
          <Button onClick={() => window.print()}>Download PDF</Button>
        </TitleMenu>

        <Subtitle>Privatlivspolitik for Salary</Subtitle>
        <p>
          <small>Den nuværende version af dette dokument er gældende fra [2026-04-13].</small>
        </p>

        <Headline>Formålet med denne politik</Headline>
        <p>
          Denne privatlivspolitik beskriver, hvordan Salary ("vi", "os" eller "vores") behandler personoplysninger i sin
          egenskab af dataansvarlig.
        </p>
        <p>
          Formålet er at informere dig om de foranstaltninger, vi implementerer for at behandle dine personoplysninger
          med den største respekt for dine rettigheder.
        </p>
        <p>
          Vi er forpligtet til, i vores behandling af dine personoplysninger, at overholde Europa-Parlamentets og Rådets
          forordning (EU) 2016/679 af 27. april 2016 om beskyttelse af fysiske personer i forbindelse med behandling af
          personoplysninger og om fri udveksling af sådanne oplysninger (herefter benævnt "GDPR"), samt alle relevante
          nationale bestemmelser.
        </p>

        <Headline>Identitet på den dataansvarlige</Headline>
        <p>
          Den enhed, der er ansvarlig for indsamling og behandling af dine personoplysninger, er:
          <br />
          Shine Salary ApS
          <br />
          CVR no. 37 62 84 09
          <br />
          Fiolstræde 17B, 1165 København K, herefter benævnt "vi" eller "Salary".
        </p>
        <p>Databeskyttelsesrådgiveren (DPO) fungerer som et fælles kontaktpunkt for alle enheder i Shine-koncernen.</p>
        <p>
          Du kan kontakte vores DPO på: <a href="mailto:dpo@shine.fr">dpo@shine.fr</a>
        </p>

        <Headline>Roller i databehandlingen</Headline>
        <p>
          I forbindelse med leveringen af de tjenester, der er nævnt i denne aftale, optræder Salary både som
          dataansvarlig og som databehandler i overensstemmelse med forordning (EU) 2016/679 ("GDPR").
        </p>

        <p>
          <strong>Når Salary optræder som dataansvarlig</strong>
          <br />
          Salary er den dataansvarlige, når vi behandler personoplysninger til vores egne formål, såsom:
        </p>
        <ul>
          <li>Medarbejderdata</li>
          <li>Information om klienter og potentielle kunder (prospects)</li>
          <li>Analyse af brug af platformen med henblik på serviceforbedring m.m.</li>
        </ul>
        <p>I disse tilfælde er denne privatlivspolitik gældende og beskriver:</p>
        <ul>
          <li>Formålene med og det juridiske grundlag for behandlingen</li>
          <li>Dine rettigheder som registreret</li>
          <li>De sikkerhedsforanstaltninger, vi implementerer for at beskytte dine personoplysninger</li>
        </ul>

        <p>
          <strong>Når Salary optræder som databehandler</strong>
          <br />
          Salary er databehandler, når vi behandler personoplysninger på vegne af vores klienter. I denne sammenhæng:
        </p>
        <ul>
          <li>Behandler vi udelukkende personoplysninger i overensstemmelse med klientens instruktioner.</li>
          <li>Behandlingen er reguleret af en databehandleraftale (DPA), som er tilgængelig via vores applikation.</li>
        </ul>

        <Headline>Datakilder, kategorier og obligatorisk indsamling af data</Headline>

        <p>
          <strong>Datakilder</strong>
          <br />
          Vi indsamler dine personoplysninger fra følgende kilder:
        </p>
        <ul>
          <li>
            Direkte fra dig, når du opretter en konto, bruger vores tjenester, udfylder formularer eller kommunikerer
            med os.
          </li>
          <li>Automatisk gennem din brug af vores hjemmeside eller platform (f.eks. cookies, logfiler, enhedsdata).</li>
          <li>
            Fra tredjeparter, såsom offentlige kilder (f.eks. LinkedIn), partnere, tjenesteudbydere og finansielle
            institutioner, hvor det er påkrævet.
          </li>
          <li>
            Via samarbejdspartnere (affiliates), når du interagerer med vores indhold på partnerhjemmesider (f.eks.
            annonceklik, henvisningsdata).
          </li>
        </ul>

        <p>
          <strong>Kategorier af indsamlede personoplysninger</strong>
          <br />
          Vi behandler følgende kategorier af personoplysninger, afhængigt af hvilken tjeneste du bruger og dit forhold
          til os:
        </p>
        <ul>
          <li>
            <strong>Identifikations- og kontaktoplysninger:</strong> For- og efternavn, e-mailadresse, telefonnummer
            (mobil eller fastnet), postadresse og land, foretrukket sprog, CPR-nummer (hvis relevant)
          </li>
          <li>
            <strong>Professionelle data:</strong> Jobtitel og funktion, virksomhedsnavn og branche, oplysninger om
            kontaktperson (for erhvervskunder)
          </li>
          <li>
            <strong>Tekniske data og brugsdata:</strong> IP-adresse, browsertype, operativsystem og
            enhedsidentifikatorer, login-tidsstempler og aktivitetslogfiler, interaktions- og navigationsdata på
            platformen, <Link to={'/' + paths.COOKIE_POLICY}>cookies og lignende sporingsdata (se Cookiepolitik)</Link>
          </li>
          <li>
            <strong>Transaktions- og finansielle data (hvor relevant):</strong> Oplysninger om abonnementer og
            betalingsstatus, fakturaer og regnskabsbilag, betalingstokens fra eksterne betalingsudbydere (vi opbevarer
            ikke kortoplysninger), klik- og konverteringsdata fra affiliate-programmer
          </li>
          <li>
            <strong>Kommunikations- og præferencedata:</strong> Markedsføringspræferencer og samtykkestatus,
            kommunikationshistorik (f.eks. supporthenvendelser, chatlogs, feedbackformularer), data fra optagede
            telefon- eller videoopkald (hvis relevant og med forudgående varsel), valg vedrørende direct marketing
            (opt-in/opt-out)
          </li>
        </ul>
        <p>
          Hvorvidt indsamlingen af data er obligatorisk eller valgfri, angives på tidspunktet for indsamlingen. Data
          markeret som obligatoriske er nødvendige for at kunne levere vores tjenester korrekt. Uden disse oplysninger
          vil din anmodning muligvis ikke kunne behandles, eller adgangen til tjenesten kan blive begrænset.
        </p>

        <Headline>Formålene med behandlingen af personoplysninger</Headline>

        <p>
          <strong>Levering af tjenesteydelser og administration af kundeforhold</strong>
          <br />
          Vi behandler dine personoplysninger for at:
        </p>
        <ul>
          <li>Registrere og administrere din brugerkonto.</li>
          <li>Levere de tjenesteydelser, som du har abonneret på via Salary-platformen.</li>
          <li>Administrere kundekontrakter, ordrer, fakturering og opfølgning på det kommercielle forhold.</li>
          <li>Identificere og verificere dig, før der gives adgang til din konto.</li>
          <li>Besvare dine forespørgsler og yde kundesupport.</li>
          <li>Håndtere ubetalte fakturaer og juridiske tvister i forbindelse med brugen af vores tjenester.</li>
        </ul>
        <p>
          Vi benytter eksterne betalingstjenesteudbydere til at behandle transaktioner. Dette betyder, at vi ikke
          opbevarer dine kreditkort- eller bankoplysninger; alle betalingsdata er krypteret og håndteres sikkert af
          vores udbydere.
        </p>
        <p>
          <em>
            Retsgrundlag (Juridisk grundlag):
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (b) (opfyldelse af kontrakt).
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (f) (legitim interesse).
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (c) (retlig forpligtelse).
          </em>
        </p>

        <p>
          <strong>Kommerciel kommunikation og markedsføring</strong>
          <br />
          Vi kan behandle dine personoplysninger til:
        </p>
        <ul>
          <li>Udsendelse af nyhedsbreve og kampagnemateriale.</li>
          <li>Kontakt med henblik på at planlægge møder eller følge op på en forespørgsel.</li>
          <li>Direkte markedsføring og kundehvervning.</li>
          <li>Segmentering af vores målgruppe for at personliggøre kommunikationen.</li>
          <li>Administration af dine præferencer for abonnementer og samtykke.</li>
        </ul>
        <p>Denne kommunikation kan:</p>
        <ul>
          <li>Sendes via e-mail, SMS eller andre elektroniske kanaler.</li>
          <li>Personliggøres baseret på din profil og dine præferencer.</li>
          <li>Tilpasses efter dine sprogindstillinger og din erhvervsprofil.</li>
        </ul>
        <p>
          Hvis du er journalist eller pressekontakt, kan vi også tilføje dig til vores presseliste, forudsat at du har
          givet dit samtykke.
        </p>
        <p>
          <em>
            Retsgrundlag (Juridisk grundlag):
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (f) (legitim interesse).
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (a) (samtykke til nyhedsbreve og presselister).
          </em>
        </p>

        <p>
          <strong>Analyse og forbedring af tjenester</strong>
          <br />
          Vi kan behandle data for at:
        </p>
        <ul>
          <li>Indsamle feedback om din oplevelse med vores tjenester.</li>
          <li>Optage telefonopkald eller digitale møder til kvalitetssikring og træning.</li>
          <li>
            Udarbejde kommerciel statistik og besøgsstatistik relateret til brugen af vores tjenester og hjemmesider.
          </li>
          <li>Udvikle og forbedre vores platformsfunktioner, ydeevne og indhold.</li>
          <li>Gennemføre tilfredshedsundersøgelser eller interne præstationsanalyser.</li>
          <li>Generere anonymiserede eller aggregerede statistikker til business intelligence.</li>
        </ul>
        <p>
          <em>
            Retsgrundlag (Juridisk grundlag):
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (f) (legitim interesse).
          </em>
        </p>

        <p>
          <strong>Overholdelse af lovgivning og risikostyring</strong>
          <br />
          Afhængigt af det marked, hvor tjenesten udbydes, og typen af det markedsførte produkt, kan vi behandle
          personoplysninger for at:
        </p>
        <ul>
          <li>Overholde forpligtelser vedrørende bekæmpelse af hvidvask (AML) og kendskab til kunden (KYC).</li>
          <li>Forebygge svindel og misbrug af regulerede tjenester.</li>
          <li>Besvare lovlige anmodninger fra tilsyns- eller reguleringsmyndigheder.</li>
          <li>Opbevare regnskabsmateriale og opfylde krav til økonomisk rapportering.</li>
          <li>
            Gennemføre interne audits og sikre overholdelse af gældende finansielle eller sektorspecifikke regler.
          </li>
        </ul>
        <p>
          Disse behandlingsaktiviteter gælder kun for specifikke tjenester og jurisdiktioner, hvor sådanne forpligtelser
          er påkrævet i henhold til lokal lovgivning eller regulatoriske rammer. Du vil blive informeret, når sådanne
          forpligtelser gælder for den tjeneste, du bruger.
        </p>
        <p>
          <em>
            Retsgrundlag (Juridisk grundlag):
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (f) (legitim interesse).
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (c) (retlig forpligtelse).
          </em>
        </p>

        <p>
          <strong>Sikkerhed for vores tjenester og infrastruktur</strong>
          <br />
          Vi implementerer foranstaltninger for at sikre sikkerheden og kontinuiteten af vores tjenester, hvilket
          indebærer behandling af personoplysninger for at:
        </p>
        <ul>
          <li>Opdage og blokere mistænkelig aktivitet eller cybertrusler.</li>
          <li>Administrere logfiler, autentificering og adgangskontrolsystemer.</li>
          <li>Udføre sikkerhedskopiering og kontrol af systemintegritet.</li>
          <li>Sikre sikker hosting og kryptering af kommunikation.</li>
        </ul>
        <p>
          <em>
            Retsgrundlag (Juridisk grundlag):
            <br />
            Databeskyttelsesforordningens artikel 6 (1) (f) (legitim interesse).
          </em>
        </p>

        <p>
          <strong>
            Valgfrie integrationer (f.eks. Overførselsservice, regnskabsværktøjer, HR-systemer,
            tidsregistreringsværktøjer)
          </strong>
          <br />
          Vi tilbyder tredjepartsværktøjer for at effektivisere brugen af platformen. Disse integrationer kræver, at vi
          sender data til tredjeparter, for at vi kan udføre dette. Integrationerne sker fuldt ud på kundens eget
          initiativ.
        </p>

        <p>
          <strong>Administration af vores hjemmeside og cookies</strong>
          <br />
          For yderligere information om behandlingen i forbindelse med administrationen af vores hjemmeside og cookies,
          henvises der til vores <Link to={'/' + paths.COOKIE_POLICY}>Cookiepolitik</Link>.
        </p>

        <p>
          <strong>Rekruttering</strong>
          <br />
          For yderligere information om behandlingen af dine personoplysninger, henvises der til privatlivspolitikken,
          som er tilgængelig på vores karriereside.
        </p>

        <p>
          Personoplysninger opbevares kun i det tidsrum, der er strengt nødvendigt for at opfylde de ovennævnte formål
          og i overensstemmelse med lovmæssige krav. Specifikke opbevaringsperioder kan oplyses efter anmodning.
        </p>

        <Headline>Modtagere af personoplysninger</Headline>
        <p>
          Vi kan gøre brug af autoriserede tjenesteudbydere, der fungerer som databehandlere på vores vegne, for at
          understøtte leveringen, driften, sikkerheden og forbedringen af vores tjenester. Disse udbydere må kun få
          adgang til personoplysninger i det omfang, det er strengt nødvendigt for at udføre deres opgaver, i
          overensstemmelse med vores dokumenterede instrukser og gældende databeskyttelseslovgivning.
        </p>
        <p>Disse udbydere omfatter, men er ikke begrænset til:</p>
        <ul>
          <li>Hosting- og cloud-tjenesteudbydere til drift og vedligeholdelse af vores servere og infrastruktur.</li>
          <li>
            Softwareudviklere til programmering, udvikling, vedligeholdelse og support af vores digitale applikationer.
          </li>
          <li>
            E-mail-tjenesteudbydere til afsendelse af transaktionsrelaterede e-mails og anden kommunikation vedrørende
            vores kontraktmæssige tjenester.
          </li>
          <li>E-mail-markedsføringsudbydere til administration og udsendelse af nyhedsbreve og kampagner.</li>
          <li>Push-notifikations- og kommunikationsudbydere.</li>
          <li>CRM-platforme (Customer Relationship Management).</li>
          <li>
            Udbydere af trafikmåling og webanalyse til vurdering af ydeevne og brug af vores hjemmeside og tjenester.
          </li>
          <li>Udbydere af annoncerings- og sporingsteknologi.</li>
          <li>Løsninger til elektronisk signatur.</li>
          <li>Udbydere af tjenester til fjernidentifikation.</li>
          <li>Platforme til administration af affiliate-programmer.</li>
          <li>Udbydere af fakturerings- og betalingstjenester.</li>
          <li>Ticketing-systemer og interne systemer til sporing af supportsager.</li>
          <li>Platforme til datavisualisering og business intelligence.</li>
          <li>IT-sikkerhedsudbydere for at sikre integriteten og beskyttelsen af vores systemer og data.</li>
          <li>
            Online-undersøgelser og chatbots til forbedring af vores tjenester og bedre forståelse af brugernes behov.
          </li>
        </ul>
        <p>Hver tjenesteudbyder er underlagt streng fortrolighed.</p>
        <p>
          Inden for rammerne af deres respektive opgaver kan vi også give adgang til dine personoplysninger til følgende
          modtagere:
        </p>
        <ul>
          <li>Interne teams, der er ansvarlige for administration og styring af løsningen.</li>
          <li>Revisions- og kontrolmyndigheder, herunder statsautoriserede revisorer.</li>
          <li>
            Betalingstjenesteudbydere, herunder kontoførere og kortudstedere, med henblik på behandling af betalinger
            eller overførsler.
          </li>
          <li>Annonceringsplatforme med henblik på at vise personliggjorte annoncer baseret på dine interesser.</li>
        </ul>
        <p>
          Som en del af Shine-koncernen kan dine personoplysninger også deles med andre enheder i koncernen, udelukkende
          når en sådan deling er nødvendig for at overholde vores retlige forpligtelser eller for at give dig tjenester,
          der er tilpasset dine specifikke behov. Listen over enheder i Shine-koncernen, der kan optræde som modtagere
          af dine personoplysninger, er tilgængelig efter anmodning på: <a href="mailto:dpo@shine.fr">dpo@shine.fr</a>
        </p>
        <p>
          Endelig kan dine personoplysninger videregives til offentlige myndigheder (f.eks. skatte- eller
          socialsikringsmyndigheder) efter anmodning for at overholde retlige forpligtelser; til retslige myndigheder,
          juridiske rådgivere eller inkassofirmaer i forbindelse med retssager eller fuldbyrdelsesforanstaltninger.
        </p>

        <Headline>Dine rettigheder efter GDPR</Headline>
        <p>Som registreret i henhold til databeskyttelsesforordningen (GDPR) har du følgende rettigheder:</p>
        <ul>
          <li>
            <strong>Ret til indsigt:</strong> Du kan anmode om indsigt i de personoplysninger, vi opbevarer om dig.
          </li>
          <li>
            <strong>Ret til berigtigelse og sletning:</strong> Du kan anmode om rettelse af ukorrekte oplysninger og
            sletning af dine personoplysninger, hvor det er relevant.
          </li>
          <li>
            <strong>Ret til begrænsning eller indsigelse mod behandling:</strong> Du kan gøre indsigelse mod
            behandlingen af dine data og anmode om, at anvendelsen af dem begrænses under visse omstændigheder.
          </li>
          <li>
            <strong>Ret til at gøre indsigelse mod direkte markedsføring:</strong> Du har en absolut ret til at gøre
            indsigelse mod brugen af dine personoplysninger til direkte markedsføringsformål.
          </li>
          <li>
            <strong>Ret til at trække samtykke tilbage:</strong> Hvor behandlingen er baseret på dit samtykke, kan du
            til enhver tid trække det tilbage, uden at det påvirker lovligheden af den behandling, der fandt sted før
            tilbagetrækningen.
          </li>
          <li>
            <strong>Ret til dataportabilitet:</strong> Du kan anmode om at modtage dine personoplysninger i et
            struktureret, almindeligt anvendt og maskinlæsbart format.
          </li>
          <li>
            <strong>Ret til at indgive en klage:</strong> Du kan indgive en klage til din nationale
            databeskyttelsesmyndighed (f.eks. Datatilsynet i Danmark).
          </li>
          <li>
            <strong>Ret til at fastsætte instruktioner efter din død (post-mortem):</strong> I visse lande kan du have
            ret til at give instruktioner om håndteringen af dine personoplysninger efter din død. Dette afhænger af
            gældende national lovgivning og status for den person, der indsender anmodningen.
          </li>
        </ul>
        <p>
          For at udøve dine rettigheder bedes du kontakte os på: <a href="mailto:dpo@shine.fr">dpo@shine.fr</a>
        </p>
        <p>
          Bemærk venligst, at udøvelsen af disse rettigheder kan være underlagt juridiske betingelser eller
          begrænsninger.
        </p>

        <Headline>Datasikkerhed og brug af kunstig intelligens</Headline>
        <p>
          Vi behandler dine personoplysninger sikkert og fortroligt i overensstemmelse med gældende
          databeskyttelseslovgivning, herunder databeskyttelsesforordningen (GDPR).
        </p>
        <p>
          Vi har implementeret passende tekniske og organisatoriske foranstaltninger for at beskytte dine data mod
          hændelig eller ulovlig tilintetgørelse, tab, ændring, offentliggørelse, uautoriseret videregivelse eller
          adgang, misbrug eller enhver anden form for ulovlig behandling. Disse foranstaltninger er designet til at
          sikre den løbende sikkerhed, integritet og fortrolighed af dine personoplysninger. Vi anvender desuden sikre
          betalingssystemer, der overholder gældende branchestandarder og lovkrav.
        </p>
        <p>
          Bemærk desuden, at Salary og vores databehandlere lejlighedsvis kan gøre brug af værktøjer baseret på kunstig
          intelligens (AI), hvilket kan indebære behandling af dine personoplysninger via algoritmer. I sådanne tilfælde
          sikrer vi, at dine data hverken direkte eller indirekte anvendes til udvikling, træning eller forbedring af
          AI-teknologier, medmindre det er strengt nødvendigt for leveringen af vores tjenester.
        </p>

        <Headline>Hosting og overførsler uden for Den Europæiske Union</Headline>
        <p>
          Vi oplyser om, at dine data opbevares og lagres i hele opbevaringsperioden på servere hos Amazon Web Services,
          som er placeret inden for Den Europæiske Union.
        </p>
        <p>
          Som en del af de værktøjer, vi anvender til at levere vores tjenester, kan visse af dine data blive overført
          til lande uden for Den Europæiske Union, herunder til USA, af vores databehandlere.
        </p>
        <p>Sådanne dataoverførsler er sikret gennem følgende garantier:</p>
        <ul>
          <li>
            Dataene overføres til et land, som af Europa-Kommissionen er vurderet til at tilbyde et tilstrækkeligt
            beskyttelsesniveau (tilstrækkelighedsafgørelse).
          </li>
          <li>
            Eller dataene overføres til et land, hvis databeskyttelsesniveau ikke er anerkendt som tilstrækkeligt i
            henhold til GDPR: I sådanne tilfælde er overførslerne baseret på passende garantier som fastsat i GDPR
            artikel 46, som er skræddersyet til den enkelte tjenesteudbyder. Disse kan omfatte, men er ikke begrænset
            til, brug af Europa-Kommissionens standardkontraktbestemmelser (SCC), implementering af bindende
            virksomhedsregler (BCR) eller tilslutning til en godkendt certificeringsmekanisme.
          </li>
          <li>Eller dataene overføres baseret på en af undtagelserne i GDPR artikel 49.</li>
        </ul>
        <p>
          For yderligere information om hosting og internationale overførsler af dine personoplysninger kan du kontakte
          os på: <a href="mailto:dpo@shine.fr">dpo@shine.fr</a>
        </p>

        <Headline>Salg af personoplysninger</Headline>
        <p>Dine personoplysninger bliver ikke solgt, udlejet eller udvekslet til fordel for tredjeparter.</p>
        <p>
          Ingen personoplysninger videregives til tredjeparter mod økonomisk eller anden form for værdifuld modydelse.
        </p>
        <p>
          Skulle denne praksis ændre sig i fremtiden, vil du udtrykkeligt blive informeret herom og få mulighed for at
          fravælge dette i overensstemmelse med de gældende lovkrav.
        </p>

        <Headline>Cookies</Headline>
        <p>
          For yderligere information om cookies henvises der til vores{' '}
          <Link to={'/' + paths.COOKIE_POLICY}>Cookiepolitik</Link>.
        </p>

        <Headline>Datatilsynet</Headline>
        <p>
          For enhver klage vedrørende dine personoplysninger har du ret til at indgive en klage til den kompetente
          databeskyttelsesmyndighed.
        </p>
        <p>For at indgive en klage til det danske Datatilsyn kan du kontakte dem på:</p>
        <p>
          Datatilsynet Carl Jacobsens Vej 35
          <br />
          2500 Valby
          <br />
          Danmark
          <br />
          +45 33 19 32 00
          <br />
          <a href="mailto:dt@datatilsynet.dk">dt@datatilsynet.dk</a>
          <br />
          <a href="https://www.datatilsynet.dk" target="_blank" rel="noreferrer">
            www.datatilsynet.dk
          </a>
        </p>

        <Headline>Ændringer af denne privatlivspolitik</Headline>
        <p>
          Vi kan fra tid til anden opdatere denne privatlivspolitik for at afspejle ændringer i vores praksis, juridiske
          krav eller af andre driftsmæssige årsager.
        </p>
        <p>
          Når vi foretager ændringer, vil vi revidere datoen for "Sidst opdateret" øverst på denne side. Hvis
          ændringerne er væsentlige, vil vi informere dig via passende kanaler, såsom e-mail eller via en meddelelse på
          vores hjemmeside, før de træder i kraft.
        </p>
        <p>
          Vi opfordrer dig til regelmæssigt at gennemlæse denne privatlivspolitik for at holde dig orienteret om,
          hvordan vi beskytter dine personoplysninger.
        </p>
      </div>
    </div>
  )
}
